import { Base } from '@studiometa/js-toolkit';
import { addClass, removeClass, lerp } from '@studiometa/js-toolkit/utils';
import { animate } from 'motion';
import { inOutCubic, inQuad } from '../../utilities/easings.js';

import ElaborationSlider from './ElaborationSlider.js';

/**
 * @typedef {object} ElaborationRef
 * @property {HTMLImageElement} placeholder
 * @property {SVGClipPathElement} clipPath
 * @property {SVGClipPathElement} clipPathRect
 * @property {SVGClipPathElement} placeholderClipPath
 * @property {SVGClipPathElement} placeholderClipPathRect
 */

/**
 * @typedef {object} ElaborationInterface
 * @property {ElaborationRef} $refs
 */

/**
 * Elaboration class
 */
export default class Elaboration extends Base {
  static config = {
    name: 'Elaboration',
    components: { ElaborationSlider },
    refs: [
      'placeholder',
      'clipPath',
      'clipPathRect',
      'placeholderClipPath',
      'placeholderClipPathRect',
    ],
  };

  static replacementSelector = '.layout-container';

  static analyticsBreadcrumbSelector = '#analytics-breadcrumb';

  static easing = inOutCubic;

  static duration = 1.2;

  /**
   * @type {HTMLElement}
   */
  content = null;

  /**
   * @type {HTMLElement}
   */
  sliderItem = null;

  /**
   * @type {HTMLImageElement}
   */
  image = null;

  /**
   * Class mounted
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  mounted() {
    // Create an empty div that will contain the new page content
    this.content = document.createElement('div');
    addClass(this.content, 'opacity-0 fixed top-0 left-0 w-screen pointer-events-none z-2');
    document.body.append(this.content);

    // Force page reload to prevent back navigation issues
    window.addEventListener('popstate', () => {
      window.location.reload();
    });
  }

  /**
   * Handles the `image-clicked` event from the child component
   * @param {import('./ElaborationSliderItem').default} sliderItem
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  onElaborationSliderItemClicked(sliderItem) {
    // User interaction feedback
    addClass(document.body, 'cursor-wait');
    addClass(this.$el, 'pointer-events-none');

    this.sliderItem = sliderItem;
    addClass(this.sliderItem.$el, 'z-2');

    // Animation for the surtitle and title exit
    animate(
      this.sliderItem.$refs.surtitleInner,
      {
        y: ['0%', '100%'],
      },
      { easing: inQuad, duration: 0.6, delay: 0 }
    );

    animate(
      this.sliderItem.$refs.titleInner,
      {
        y: ['0%', '100%'],
      },
      { easing: inQuad, duration: 0.5, delay: 0.15 }
    );

    // Set the placeholder src
    this.image = this.sliderItem.$refs.img;
    this.$refs.placeholder.src = this.image.querySelector('img').src;
  }

  /**
   * Handle the load event on the placeholder
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  async onPlaceholderLoad() {
    // Get the new page HTML content
    const url = this.sliderItem.$el.getAttribute('href');
    const html = await (await fetch(url)).text();

    // Parse the content and embed it in the current page
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    this.content.innerHTML = newDocument.body.querySelector(
      Elaboration.replacementSelector
    ).innerHTML;

    // Navigate to the new page
    window.history.pushState('details', newDocument.title, url);
    document.title = newDocument.title;

    // Trigger the page change animation
    this.animate();

    const analyticsBreadcrumb = newDocument.body.querySelector(
      Elaboration.analyticsBreadcrumbSelector
    )?.innerHTML;

    this.sendAnalytics(newDocument.title, analyticsBreadcrumb);
  }

  /**
   * Page change animation
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  animate() {
    // Compute the transform matrix from and to the placeholder to the target and current image.
    const targetImage = this.content.querySelector('#cover');
    const fromTransformMatrix = this.getTransformMatrix(this.$refs.placeholder, this.image);
    const toTransformMatrix = this.getTransformMatrix(this.$refs.placeholder, targetImage);

    // Show the placeholder instead of the current image
    removeClass(this.$refs.placeholder, 'opacity-0');
    addClass(this.image, '!opacity-0');

    const scale =
      targetImage.clientHeight /
      targetImage.clientWidth /
      (this.image.clientHeight / this.image.clientWidth);

    // Crop the placeholder cip path to match the target image
    animate(
      (progress) => {
        this.$refs.placeholderClipPathRect.setAttribute('x', lerp(0.05, 0, progress));
        this.$refs.placeholderClipPathRect.setAttribute(
          'y',
          lerp(0.05, 0.5 * (1 - scale), progress)
        );
        this.$refs.placeholderClipPathRect.setAttribute('width', lerp(0.9, 1, progress));
        this.$refs.placeholderClipPathRect.setAttribute('height', lerp(0.9, scale, progress));

        // Scale and position the placeholder to match the target image
        const scaleXY = lerp(fromTransformMatrix.scale, toTransformMatrix.scale, progress);
        const x = lerp(fromTransformMatrix.x, toTransformMatrix.x, progress);
        const y = lerp(fromTransformMatrix.y, toTransformMatrix.y, progress);
        const opacity = lerp(0.6, 1, progress);

        this.$refs.placeholder.style.opacity = opacity;
        this.$refs.placeholder.style.transform = `translate(${x}px, ${y}px) scale(${scaleXY})`;
      },
      { duration: Elaboration.duration, easing: Elaboration.easing }
    ).finished.then(() => {
      // Trigger the cleanup operation
      this.complete();
    });
  }

  /**
   * Complete/cleanup
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  complete() {
    // Show the loaded content
    animate(
      this.content,
      {
        opacity: 1,
      },
      { duration: 0.8 }
    ).finished.then(() => {
      // Replace the content with the content from the new page
      document.body.querySelector(Elaboration.replacementSelector).innerHTML =
        this.content.innerHTML;

      // Ensure that scrollBehavior is disabled and scroll to top
      document.documentElement.style.scrollBehavior = 'unset';
      window.scrollTo({ top: 0 });
      document.documentElement.style.scrollBehavior = '';

      // Reload the javascript and reset
      removeClass(document.body, 'cursor-wait');
      this.content.innerHTML = '';
      this.$root.$update();
    });
  }

  /**
   * Compute the transform matrix from a base element to a target element
   * @param   {HTMLElement} base
   * @param   {HTMLElement} target
   * @this    {Elaboration & ElaborationInterface}
   * @returns {void}
   */
  getTransformMatrix(base, target) {
    const originalDimensions = base.getBoundingClientRect();
    const targetDimensions = target.getBoundingClientRect();

    const scale = Math.max(
      targetDimensions.width / originalDimensions.width,
      targetDimensions.height / originalDimensions.height
    );

    return {
      scale,
      x:
        targetDimensions.x +
        0.5 * targetDimensions.width -
        (originalDimensions.x + 0.5 * originalDimensions.width),
      y:
        targetDimensions.y +
        0.5 * targetDimensions.height -
        (originalDimensions.y + 0.5 * originalDimensions.height),
    };
  }

  /**
   * Send analytics.
   * @param {string} pageTitle
   * @param {Array|null} breadcrumb
   * @returns {void}
   */
  sendAnalytics(pageTitle, breadcrumb = null) {
    if (typeof window.pa !== 'object') {
      return;
    }

    if (!pageTitle || !breadcrumb) {
      return;
    }

    const breadcrumbParsed = JSON.parse(breadcrumb) ?? [];

    const page = pageTitle.split(' | ')[0];

    if (!breadcrumbParsed.includes(page)) {
      breadcrumbParsed.push(page);
    }

    if (breadcrumbParsed.length > 1) {
      breadcrumbParsed.shift();
    }

    const data = { page };
    breadcrumbParsed.forEach((name, i) => {
      if (i > 2) {
        return;
      }

      data[`page_chapter${i + 1}`] = name;
    });

    window.pa.sendEvent('page.display', data);
  }
}
